import type { i18n as I18nInstance, Resource } from 'i18next';
import i18next from 'i18next';
// Contexto e gancho pelo next-i18next, não por 'react-i18next' direto: no servidor o next-i18next é carregado do
// node_modules (CommonJS) e o 'react-i18next' importado do código vai no pacote do webpack (ESM) — dois módulos, dois
// contextos. Pelo next-i18next este provedor e o useTranslation dos blocos usam o MESMO contexto nos dois lados.
import { I18nContext, useTranslation } from 'next-i18next';
import type { ReactNode } from 'react';
import { useMemo } from 'react';

/**
 * Instância i18next própria da página de um par, igual no servidor e no navegador: só os textos do par, sem idioma de
 * reserva (`fallbackLng: false`, `load: 'currentOnly'`), como o `_nextI18Next` da página pede (src/utils/home-intl/i18n.ts).
 *
 * Por quê: no servidor o next-i18next CLONA a instância global do site (`cloneInstance`), e o clone herda os
 * `languageUtils` da global — com a reserva do site (`fallbackLng: { default: ['en'] }`). Uma chave que o par não tem
 * (ex.: `games.cod-warzone` do #jogos-por-game no /br/pt) saía em INGLÊS no HTML do servidor, enquanto o navegador (só os
 * textos do par) não achava a chave: cartões a mais no servidor, hidratação quebrada (React #418/#423) e texto em inglês
 * numa página em outro idioma. Com esta instância os dois lados resolvem as chaves do mesmo jeito.
 */
export function createPairI18n(lng: string, resources: Resource): I18nInstance {
  const namespaces = Object.keys(resources[lng] ?? {});
  const instance = i18next.createInstance();
  // initImmediate false + resources inline: init síncrono (a instância já serve no 1º render)
  instance.init({ lng, resources, ns: namespaces, defaultNS: false, fallbackLng: false, load: 'currentOnly', supportedLngs: false, interpolation: { escapeValue: false }, react: { useSuspense: false }, initImmediate: false });
  return instance;
}

/** Envolve a página do par com a instância acima (lng = `homeIntlPage.i18nLng`, textos já no store da página). */
export function HomeIntlI18nBoundary({ lng, children }: { lng: string; children: ReactNode }) {
  const { i18n } = useTranslation();
  const bundle = i18n?.store?.data?.[lng];
  // sem os textos do par no store (não acontece numa página de par): segue com a instância da página
  const value = useMemo(() => ({ i18n: bundle ? createPairI18n(lng, { [lng]: bundle }) : i18n }), [lng, bundle, i18n]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
